import React, { type FC, useState, useEffect } from 'react';
import { useAuth  } from 'components/Auth';
import { useTranslation } from 'react-i18next';
import { PageMetadata } from 'components/Shared/PageMetadata';
import { useAppConfig } from 'providers/AppConfig';
import { BrowserRouter } from 'react-router-dom';
import { AppRouting } from './AppRouting';
import { NewVersionMessage } from 'components/Layout';
import { TelemetryNavigationTracker, initializeTelemetry } from './TelemetryInit';
import { useRegionConfig } from 'providers';
import { NavigationGuardProvider } from 'providers/NavigationGuard';


declare const AppVersion: string;

interface AppProps {}

export const App: FC<AppProps> = () => {
    const { providerData } = useAuth();
    const [newVersionFound, setNewVersionFound] = useState(false);
    const { t, ready } = useTranslation('common', { useSuspense: false });
    const config = useAppConfig();
    const regionCode = useRegionConfig()?.defaultLng?.split('-')?.[1]?.toLowerCase();

    const hardReload = async () => {
        await fetch('/', {
            method: 'GET',
            headers: {
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache',
            }
        });
        window.location.pathname = '/';
        window.location.reload();
    };

    useEffect(() => {
        if ((AppVersion !== '#{AppVersion}') && (AppVersion !== config.AppVersion)) {
            setNewVersionFound(true);
        }
    }, [config.AppVersion, AppVersion]);

    useEffect(() => {
        initializeTelemetry(providerData, regionCode, config.Dynatrace?.env, config.AppVersion);
    }, [config.Dynatrace?.env, providerData?.providerId, config.AppVersion]);

    return (
        <>
            {
                ready &&
                <PageMetadata
                    pageDescription={t('page_description')}
                    pageTitle={t('page_title')}
                />
            }
            {
                newVersionFound &&
                <NewVersionMessage handleReloadClick={hardReload}/>
            }
            <BrowserRouter>
                <TelemetryNavigationTracker>
                    <NavigationGuardProvider>
                        <AppRouting />
                    </NavigationGuardProvider>
                </TelemetryNavigationTracker>
            </BrowserRouter>
        </>
    )
}

export default App;
